// Environment configuration
export const config = {
  // API Configuration
  graphqlEndpoint: process.env.REACT_APP_GRAPHQL_ENDPOINT || 'https://localhost:5001/graphql/v1',
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://localhost:5001',
  
  // Azure B2C Configuration
  msalConfig: {
    auth: {
      clientId: process.env.REACT_APP_B2C_CLIENT_ID || '5a4fe43b-e17b-42c9-8ff9-5971213798fb',
      authority: process.env.REACT_APP_B2C_AUTHORITY || 'https://dommicrosso.b2clogin.com/dommicrosso.onmicrosoft.com/B2C_1A_SIGNUP_SIGNIN',
      redirectUri: process.env.REACT_APP_B2C_REDIRECT_URI || 'http://localhost:3000',
      postLogoutRedirectUri: process.env.REACT_APP_B2C_REDIRECT_URI || 'http://localhost:3000',
      knownAuthorities: [
        process.env.REACT_APP_B2C_KNOWN_AUTHORITY || 'dommicrosso.b2clogin.com'
      ],
      validateAuthority: false, // Important for B2C
      navigateToLoginRequestUrl: false // Prevents redirect loops
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: any, message: string, containsPii: boolean) => {
          if (containsPii) return;
          switch (level) {
            case 0: // Error
              console.error('[MSAL ERROR]', message);
              break;
            case 1: // Warning
              console.warn('[MSAL WARNING]', message);
              break;
            case 2: // Info
              console.info('[MSAL INFO]', message);
              break;
            case 3: // Verbose
              if (process.env.NODE_ENV === 'development') {
                console.debug('[MSAL DEBUG]', message);
              }
              break;
          }
        },
        piiLoggingEnabled: false,
        logLevel: process.env.NODE_ENV === 'development' ? 3 : 2 // Verbose in dev, Info in prod
      }
    }
  },
  
  // Login request configuration
  loginRequest: {
    scopes: [
      process.env.REACT_APP_B2C_SCOPE || 'https://dommicrosso.onmicrosoft.com/TalkForTy/read'
    ],
    prompt: 'select_account' // Better for B2C
  },
  
  // Token request configuration
  tokenRequest: {
    scopes: [
      process.env.REACT_APP_B2C_SCOPE || 'https://dommicrosso.onmicrosoft.com/TalkForTy/read'
    ]
  },
  
  // App Configuration
  appName: 'TalkForTy Control Panel',
  version: '1.0.0',
  
  // Feature flags
  features: {
    enableDarkMode: true,
    enableNotifications: true,
    enableAudioUpload: true,
    enableImageUpload: true
  }
};

// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_B2C_CLIENT_ID',
  'REACT_APP_B2C_AUTHORITY',
  'REACT_APP_B2C_SCOPE'
];

const missingEnvVars = requiredEnvVars.filter(envVar => !process.env[envVar]);

if (missingEnvVars.length > 0) {
  console.warn('Missing required environment variables:', missingEnvVars);
  console.warn('Please ensure .env file contains the required B2C configuration');
}

// B2C Policies configuration
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1A_SIGNUP_SIGNIN",
    forgotPassword: "B2C_1A_PASSWORDRESET",
    editProfile: "B2C_1A_PROFILEEDIT",
  },
  authorities: {
    signUpSignIn: {
      authority: "https://dommicrosso.b2clogin.com/dommicrosso.onmicrosoft.com/B2C_1A_SIGNUP_SIGNIN",
    },
    forgotPassword: {
      authority: "https://dommicrosso.b2clogin.com/dommicrosso.onmicrosoft.com/B2C_1A_PASSWORDRESET",
    },
    editProfile: {
      authority: "https://dommicrosso.b2clogin.com/dommicrosso.onmicrosoft.com/B2C_1A_PROFILEEDIT",
    },
  },
  authorityDomain: "dommicrosso.b2clogin.com",
};

// Set the default authority to sign up/sign in policy
config.msalConfig.auth.authority = b2cPolicies.authorities.signUpSignIn.authority;
config.msalConfig.auth.knownAuthorities = [b2cPolicies.authorityDomain];

// Development mode logging
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Azure B2C Configuration:', {
    clientId: config.msalConfig.auth.clientId,
    authority: config.msalConfig.auth.authority,
    redirectUri: config.msalConfig.auth.redirectUri,
    scopes: config.loginRequest.scopes
  });
}

// Test B2C endpoint availability
const testB2CEndpoint = async () => {
  try {
    const wellKnownUrl = `${config.msalConfig.auth.authority}/.well-known/openid-configuration`;
    console.log('🔍 Testing B2C endpoint:', wellKnownUrl);
    
    const response = await fetch(wellKnownUrl);
    if (response.ok) {
      const data = await response.json();
      console.log('✅ B2C endpoint is accessible');
      console.log('📋 B2C Configuration:', {
        issuer: data.issuer,
        authorization_endpoint: data.authorization_endpoint,
        token_endpoint: data.token_endpoint,
        userinfo_endpoint: data.userinfo_endpoint
      });
      return true;
    } else {
      console.error('❌ B2C endpoint failed:', response.status, response.statusText);
      return false;
    }
  } catch (error) {
    console.error('❌ B2C endpoint error:', error);
    return false;
  }
};

// Run the test in development
if (process.env.NODE_ENV === 'development') {
  testB2CEndpoint().then(success => {
    if (success) {
      console.log('🎉 B2C configuration appears valid');
    } else {
      console.log('⚠️  B2C configuration may need attention');
    }
  });
}
